import { useState } from 'react';

import type { Tischplan } from '../api';
import { formatiereEuro } from '../warenkorb';

export interface TischBelegung {
  summeCent: number;
  /** ISO-Zeit, seit der der Tisch belegt ist. */
  seit: string;
  bediener: string | null;
  /** nur auf diesem Gerät geparkt (offline) */
  lokal: boolean;
}

function minutenSeit(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
}

/**
 * Tischplan an der Kasse: Bereiche als Reiter, Tische an ihrer Stelle im Raster.
 * Frei = hell, belegt = blau mit Betrag und Minuten seit dem Parken, der gerade
 * offene Tisch ist hervorgehoben. Antippen öffnet bzw. wählt den Tisch.
 */
export function TischplanAnsicht(props: {
  plan: Tischplan;
  raster: { breite: number; hoehe: number };
  belegung: ReadonlyMap<string, TischBelegung>;
  aktiverTisch: string | null;
  onWahl: (name: string) => void;
  onSchliessen: () => void;
}) {
  const { plan, raster } = props;
  const [bereich, setBereich] = useState(() => {
    const i = plan.bereiche.findIndex((b) => b.tische.some((t) => t.name === props.aktiverTisch));
    return Math.max(0, i);
  });
  const aktuell = plan.bereiche[bereich];
  const belegtImBereich = (i: number): number =>
    plan.bereiche[i]?.tische.filter((t) => props.belegung.has(t.name)).length ?? 0;

  return (
    <div className="zahlung-dialog tischplan-dialog">
      <div className="karte">
        <div className="tischplan-kopf">
          <div className="chips" role="group" aria-label="Bereiche">
            {plan.bereiche.map((b, i) => (
              <button
                key={b.name}
                type="button"
                className={i === bereich ? 'aktiv' : ''}
                onClick={() => {
                  setBereich(i);
                }}
              >
                {b.name}
                {belegtImBereich(i) > 0 && ` (${String(belegtImBereich(i))})`}
              </button>
            ))}
          </div>
          <button type="button" className="taste zweit" onClick={props.onSchliessen}>
            Schließen
          </button>
        </div>
        {aktuell !== undefined && (
          <div
            className="tischplan-raum"
            style={{ aspectRatio: `${String(raster.breite)} / ${String(raster.hoehe)}` }}
          >
            {aktuell.tische.map((t) => {
              const b = props.belegung.get(t.name);
              return (
                <button
                  key={t.name}
                  type="button"
                  className={`raum-tisch ${t.form}${b === undefined ? '' : ' belegt'}${props.aktiverTisch === t.name ? ' aktiv' : ''}`}
                  style={{
                    left: `${String((t.x / raster.breite) * 100)}%`,
                    top: `${String((t.y / raster.hoehe) * 100)}%`,
                    width: `${String((t.b / raster.breite) * 100)}%`,
                    height: `${String((t.h / raster.hoehe) * 100)}%`,
                  }}
                  title={
                    b === undefined
                      ? `${t.name} – frei`
                      : `${t.name} – ${formatiereEuro(b.summeCent)} €${b.bediener === null ? '' : `, ${b.bediener}`}${b.lokal ? ' (nur dieses Gerät)' : ''}`
                  }
                  onClick={() => {
                    props.onWahl(t.name);
                  }}
                >
                  <strong>{t.name}</strong>
                  {b !== undefined && (
                    <>
                      <span>{formatiereEuro(b.summeCent)} €</span>
                      <small>{minutenSeit(b.seit)} min</small>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
